import React, { useEffect } from 'react';
import { ISubmissionData } from '@root/types';
import styled from 'styled-components';
import { Button, Typography, Grid, useMediaQuery } from '@material-ui/core';
import { IAppState } from '../../types';
import {
  StyledCard,
  CardHeader,
  CardBody,
  QuestionTypography,
} from '../../styles/CardTextAndContainers';
import getResultText from '../../utils/getResultText';
import getNoneScoredAnswers from '../../utils/getNonScoredAnswers';
import { getAnswersWithScoringDetails } from '../../utils/getFinalScore';
import getRange from '../../utils/getRange';

interface IProps {
  state: IAppState;
}

const ResultCard = ({ state }: IProps) => {
  const { text, resultTitle } = getResultText(state.score, state.helpedByBus);
  const largeScreen = useMediaQuery('(min-width:450px)');

  const {
    region,
    industry,
    email,
    name,
    company,
    helpedByBuS,
    title,
    yearWithDistributor,
    howDidYouFindDistributor,
    howManyMarketsDoYouHaveDistributors,
    firstMarketExpansion,
  } = getNoneScoredAnswers(state.userFormInput);

  useEffect(() => {
    const submitUserInput = async () => {
      const data: ISubmissionData = {
        answers: getAnswersWithScoringDetails(state.userFormInput),
        range: getRange(state.score),
        score: state.score,
        helpedByBus: helpedByBuS,
        region,
        industry,
        email,
        name,
        company,
        title,
        yearWithDistributor,
        howDidYouFindDistributor,
        howManyMarketsDoYouHaveDistributors,
        firstMarketExpansion,
      };

      const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      };

      fetch('/api/answers', requestOptions)
        .then((response) => response.text())
        .catch((error) => console.log('error', error));
    };
    submitUserInput();
  }, [
    company,
    helpedByBuS,
    email,
    industry,
    name,
    region,
    state.score,
    state.userFormInput,
    title,
    yearWithDistributor,
    howDidYouFindDistributor,
    howManyMarketsDoYouHaveDistributors,
    firstMarketExpansion,
  ]);

  return (
    <StyledCard>
      <CardHeader>Your results!</CardHeader>
      <CardBody>The distributor you have assessed scores:</CardBody>
      <ScoreContainer>
        <ScoreTypography>{state.score} / 100</ScoreTypography>
      </ScoreContainer>
      <QuestionTypography>{resultTitle}</QuestionTypography>
      <CardBody
        data-testid="result-card-body"
        dangerouslySetInnerHTML={{ __html: text }}
      />
      <Typography style={{ fontStyle: 'italic', marginBottom: '18px' }}>
        Let us inspire and support you to optimise sales globally
      </Typography>
      <CardBody>
        <ButtonGroupContainer>
          <ButtonContainer>
            <Button
              style={{
                textAlign: 'center',
                backgroundColor: '#03274a',
                color: '#fff',
                width: largeScreen ? '500px' : '350px',
              }}
              href="https://www.business-sweden.com/forms/contact-form/?contactid=7550&utm_campaign=1052&utm_medium=organic&utm_source=contact&utm_content=distributor"
              target="_blank"
            >
              <Grid container direction="column">
                <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
                  Contact us
                </div>
                <div
                  style={{
                    fontSize: '10px',
                  }}
                >
                  Asli Sungur, Head of Region Sweden Advisory
                </div>
              </Grid>
            </Button>
          </ButtonContainer>
          <ButtonContainer>
            <Button
              style={{
                textAlign: 'center',
                backgroundColor: '#03274a',
                color: '#fff',
                width: largeScreen ? '500px' : '350px',
              }}
              href="https://www.business-sweden.com/contentassets/93ec4fcd15434133aec997007aa6bdd8/decoding-distributor-dynamics.pdf?utm_campaign=1052&utm_medium=organic&utm_source=report&utm_content=distributor"
              target="_blank"
            >
              <ButtonTextContainer>
                <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
                  Read our report
                </div>
                <div
                  style={{
                    fontSize: '10px',
                  }}
                >
                  Decoding Distributor Dynamics
                </div>
              </ButtonTextContainer>
            </Button>
          </ButtonContainer>
          <ButtonContainer>
            <Button
              style={{
                textAlign: 'center',
                backgroundColor: '#03274a',
                color: '#fff',
                width: largeScreen ? '500px' : '350px',
              }}
              href="https://www.business-sweden.com/services/sales-acceleration/?utm_campaign=1052&utm_medium=organic&utm_source=service&utm_content=distributor"
              target="_blank"
            >
              <Grid container direction="column">
                <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
                  Explore our services
                </div>
                <div style={{ fontSize: '10px' }}>
                  Sales acceleration in global markets
                </div>
              </Grid>
            </Button>
          </ButtonContainer>
        </ButtonGroupContainer>
      </CardBody>
    </StyledCard>
  );
};

const ScoreContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`;
const ScoreTypography = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 60px;
  font-style: normal;
  font-weight: 700;
  line-height: 72px;
  letter-spacing: -0.5000000596046448px;
  text-align: left;
`;

const ButtonContainer = styled.div`
  margin-bottom: 18px;
  width: 600px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  @media (max-width: 450px) {
    width: 345px;
  }
`;

const ButtonTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const ButtonGroupContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

export default ResultCard;
